// v3 - fonte unificada e-commerce + comparação período anterior + breakdown status (2026-06-16)
import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { fetchVendasRecentesTodas, fetchEcommerceBlingDashboard, fetchMovimentacoesDetalhe } from '../../services/api';
import ProductImageTooltip from '../../components/ProductImageTooltip/ProductImageTooltip';
import MovimentacoesModal from '../../components/MovimentacoesModal/MovimentacoesModal';
import DatePickerWithData from '../../components/DatePickerWithData/DatePickerWithData';
import styles from './Home.module.css';

// ── helpers de período anterior ──────────────────────────────────────────────

/**
 * Dado um intervalo [start, end], retorna o MESMO período do ANO ANTERIOR (YoY).
 * Ex.: 09-16/06/2026 → 09-16/06/2025.
 * Comparação ano a ano respeita a sazonalidade do negócio (mesmas datas, -1 ano).
 */
function calcularPeriodoAnterior(dataInicio, dataFim) {
  const start = new Date(dataInicio + 'T00:00:00');
  const end   = new Date(dataFim   + 'T00:00:00');
  start.setFullYear(start.getFullYear() - 1);
  end.setFullYear(end.getFullYear() - 1);
  return {
    dataInicio: start.toISOString().split('T')[0],
    dataFim:    end.toISOString().split('T')[0],
  };
}

function calcularVariacao(atual, anterior) {
  if (!anterior || anterior === 0) return null;
  return ((atual - anterior) / anterior) * 100;
}

// ── componente de variação ────────────────────────────────────────────────────

const VariacaoBadge = ({ pct }) => {
  if (pct === null || pct === undefined) return null;
  const positivo = pct >= 0;
  const cor = positivo ? '#27ae60' : '#e74c3c';
  const seta = positivo ? '▲' : '▼';
  return (
    <span style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: '3px',
      fontSize: '12px',
      fontWeight: 700,
      color: cor,
      background: positivo ? 'rgba(39,174,96,0.10)' : 'rgba(231,76,60,0.10)',
      border: `1px solid ${positivo ? 'rgba(39,174,96,0.25)' : 'rgba(231,76,60,0.25)'}`,
      borderRadius: '6px',
      padding: '2px 6px',
      marginLeft: '8px',
      verticalAlign: 'middle',
    }}>
      {seta} {Math.abs(pct).toFixed(1)}%
    </span>
  );
};

// ── card de resumo por canal (2026-08-25 — redesenhado para bater com o mockup) ──
// Substitui o antigo MetricCard (Bootstrap Card genérico com ícone lateral, layout
// diferente do mockup). Mantém o MESMO comportamento de tooltip no hover (dados de
// composição do valor) via OverlayTrigger/Tooltip do react-bootstrap.
const ResumoCard = ({ titulo, valor, variacaoPct, subtitulo, tooltip, dark }) => {
  const cardContent = (
    <div className={`${styles.resumoCard} ${dark ? styles.resumoCardDark : ''}`}>
      <div className={styles.resumoCardTop}>
        <span className={styles.resumoCardTitulo}>{titulo}</span>
        <VariacaoBadge pct={variacaoPct} />
      </div>
      <div className={styles.resumoCardValor}>{valor}</div>
      {subtitulo && <div className={styles.resumoCardSub}>{subtitulo}</div>}
    </div>
  );

  if (!tooltip) return cardContent;

  return (
    <OverlayTrigger
      placement="bottom"
      overlay={
        <Tooltip id={`tt-resumo-${titulo}`} className={styles.resumoTooltip}>
          {tooltip}
        </Tooltip>
      }
    >
      {cardContent}
    </OverlayTrigger>
  );
};

// ── componente de status badge ────────────────────────────────────────────────

const STATUS_CONFIG = {
  atendido:  { label: 'Atendido',      cor: '#27ae60', bg: '#0d2b1a' },
  andamento: { label: 'Em Andamento',  cor: '#f39c12', bg: '#2b1f0a' },
  aberto:    { label: 'Em Aberto',     cor: '#3498db', bg: '#0a1a2b' },
  cancelado: { label: 'Cancelado',     cor: '#e74c3c', bg: '#2b0a0a' },
};

function normalizarStatus(status) {
  if (!status) return 'atendido';
  const s = status.toLowerCase().trim();
  if (s.includes('cancel')) return 'cancelado';
  if (s.includes('aberto') || s.includes('pendente') || s.includes('aguard') || s.includes('indefinido')) return 'aberto';
  if (s.includes('andamento') || s.includes('separac') || s.includes('enviado') || s.includes('transito') || s.includes('entrega') || s.includes('expedi')) return 'andamento';
  return 'atendido';
}

// ── Home ──────────────────────────────────────────────────────────────────────

const Home = () => {

  // vendas de loja física (Centro + Caxias) — fonte: fetchVendasRecentesTodas
  const [vendasLojaFisica, setVendasLojaFisica] = useState({ centro: [], caxias: [], movimentacoes: {} });
  // pedidos e-commerce — fonte: fetchEcommerceBlingDashboard → pedidosPeriodo (pedidos_ecommerce)
  const [pedidosEcommerceRaw, setPedidosEcommerceRaw] = useState([]);
  // statusBreakdown do período atual
  const [statusBreakdown, setStatusBreakdown] = useState({
    atendido: { valor: 0, count: 0 },
    emAberto: { valor: 0, count: 0 },
    cancelado: { valor: 0, count: 0 },
  });
  // resumo do e-commerce (totalPedidos, valorTotal, ticketMedio)
  const [resumoEcommerce, setResumoEcommerce] = useState({ totalPedidos: 0, valorTotal: 0, ticketMedio: 0 });

  // totais do período ANTERIOR para comparação ▲/▼
  const [totaisAnteriores, setTotaisAnteriores] = useState({
    ecommerce: null,
    centro: null,
    caxias: null,
  });

  const [loading, setLoading] = useState(true);
  const [ultimaAtualizacao, setUltimaAtualizacao] = useState(null);

  // Estado para pedidos expandidos
  const [pedidosExpandidos, setPedidosExpandidos] = useState({});
  // "Ver todos os pedidos" (mockup 2026-08-25): cada coluna começa com a lista limitada
  // (scroll interno, como já era), e ao clicar remove o limite pra mostrar tudo sem
  // precisar rolar dentro da caixinha — é um recorte real, não um link decorativo.
  const [colunasExpandidas, setColunasExpandidas] = useState({});

  // Filtro de data — padrão últimos 7 dias
  const hoje = new Date();
  const seteDiasAtras = new Date(hoje);
  seteDiasAtras.setDate(seteDiasAtras.getDate() - 7);

  const [dataInicio, setDataInicio] = useState(seteDiasAtras.toISOString().split('T')[0]);
  const [dataFim, setDataFim] = useState(hoje.toISOString().split('T')[0]);

  // Modal de movimentações (cancelamentos / trocas)
  const [modalMovimentacoes, setModalMovimentacoes] = useState({ show: false, tipo: 'cancelamentos', loja: '', codLocal: '' });
  // Modal de detalhe de status do e-commerce (clicar num badge → lista os pedidos daquele status)
  const [modalStatusEcom, setModalStatusEcom] = useState({ show: false, statusKey: null });

  const carregarVendas = async () => {
    setLoading(true);
    setColunasExpandidas({});
    try {
      const periodoAnterior = calcularPeriodoAnterior(dataInicio, dataFim);

      // Paralelo: período atual (loja física + e-commerce) + período anterior (só totais)
      const [
        vendasResp,
        dashResp,
        dashAnteriorResp,
        vendasAnterioresResp,
      ] = await Promise.all([
        fetchVendasRecentesTodas(999999, dataInicio, dataFim),
        fetchEcommerceBlingDashboard({ dataInicio, dataFim }),
        fetchEcommerceBlingDashboard({ dataInicio: periodoAnterior.dataInicio, dataFim: periodoAnterior.dataFim }),
        fetchVendasRecentesTodas(999999, periodoAnterior.dataInicio, periodoAnterior.dataFim),
      ]);

      // ── Loja física (Centro + Caxias) ─────────────────────────────────────
      if (vendasResp.success && vendasResp.data) {
        setVendasLojaFisica({
          centro: vendasResp.data.centro || [],
          caxias: vendasResp.data.caxias || [],
          movimentacoes: vendasResp.data.movimentacoes || {},
        });
      }

      // ── E-commerce atual ──────────────────────────────────────────────────
      if (dashResp.success && dashResp.data) {
        const { resumo, pedidosPeriodo } = dashResp.data;
        if (resumo?.statusBreakdown) setStatusBreakdown(resumo.statusBreakdown);
        if (resumo) setResumoEcommerce({
          totalPedidos: resumo.totalPedidos || 0,
          valorTotal:   resumo.valorTotal   || 0,
          ticketMedio:  resumo.ticketMedio   || 0,
        });
        // Fonte única: pedidosPeriodo de pedidos_ecommerce
        setPedidosEcommerceRaw(pedidosPeriodo || []);
      }

      // ── Totais do período anterior ─────────────────────────────────────────
      // E-commerce anterior: resumo.valorTotal (ex-cancelados não descontados aqui — mesma lógica)
      let ecommerceAnterior = null;
      if (dashAnteriorResp.success && dashAnteriorResp.data?.resumo) {
        const r = dashAnteriorResp.data.resumo;
        ecommerceAnterior = (r.statusBreakdown?.atendido?.valor || 0) + (r.statusBreakdown?.emAberto?.valor || 0);
        if (ecommerceAnterior === 0) ecommerceAnterior = r.valorTotal || 0;
      }

      // Centro / Caxias anteriores: soma itens (mesma lógica do período atual)
      let centroAnterior = null;
      let caxiasAnterior = null;
      if (vendasAnterioresResp.success && vendasAnterioresResp.data) {
        const d = vendasAnterioresResp.data;
        centroAnterior = (d.centro || []).reduce((acc, v) =>
          acc + (parseFloat(v.valorTotal) || 0) - (parseFloat(v.valorDesconto) || 0), 0);
        caxiasAnterior = (d.caxias || []).reduce((acc, v) =>
          acc + (parseFloat(v.valorTotal) || 0) - (parseFloat(v.valorDesconto) || 0), 0);
      }

      setTotaisAnteriores({
        ecommerce: ecommerceAnterior,
        centro:    centroAnterior,
        caxias:    caxiasAnterior,
      });

      setUltimaAtualizacao(new Date());
    } catch (error) {
      console.error('Erro ao carregar vendas:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregarVendas();
  }, []);

  // ── Pedidos e-commerce agrupados (fonte única: pedidosPeriodo) ─────────────
  // pedidosPeriodo já vem com itens[] em batch — não há N+1
  const pedidosEcommerce = useMemo(() => {
    return pedidosEcommerceRaw.map(p => {
      // Agrupar itens duplicados (mesmo código)
      const itensMap = {};
      for (const item of (p.itens || [])) {
        const cod = item.codigoProduto;
        if (!itensMap[cod]) {
          itensMap[cod] = { ...item };
        } else {
          itensMap[cod].quantidade += item.quantidade || 0;
          itensMap[cod].valorTotal  += item.valorTotal  || 0;
        }
      }
      return {
        id:             p.numero,   // número do pedido Bling (não row id)
        dataVenda:      p.dataVenda,
        loja:           'E-commerce',
        itens:          Object.values(itensMap),
        total:          p.valorTotal || 0,
        pedidoTotal:    p.valorTotal || 0,
        frete:          p.frete      || 0,
        desconto:       0,
        quantidadeTotal: p.totalPecas || (p.itens || []).reduce((a, i) => a + (i.quantidade || 0), 0),
        status:         p.status     || 'Pendente',
      };
    });
  }, [pedidosEcommerceRaw]);

  // ── Loja física agrupada (inalterado) ─────────────────────────────────────
  const agruparPorPedido = useCallback((listaVendas, tipoLoja) => {
    const pedidosMap = {};

    listaVendas.forEach(venda => {
      const pedidoId = venda.boleta || venda.pedidoId || `item-${venda.dataVenda}-${venda.codigoProduto}`;

      if (!pedidosMap[pedidoId]) {
        pedidosMap[pedidoId] = {
          id: pedidoId,
          dataVenda: venda.dataVenda,
          loja: tipoLoja,
          itens: [],
          itensMap: {},
          total: 0,
          pedidoTotal: parseFloat(venda.pedidoTotal) || 0,
          frete: parseFloat(venda.valorFrete) || 0,
          desconto: 0,
          quantidadeTotal: 0,
          status: venda.status || 'Atendido',
        };
      }

      const codigoProduto = venda.codigoProduto;
      const quantidade    = parseInt(venda.quantidade)   || 1;
      const valorUnitario = parseFloat(venda.valorUnitario) || 0;
      const valorTotal    = parseFloat(venda.valorTotal)    || 0;

      if (!pedidosMap[pedidoId].itensMap[codigoProduto]) {
        pedidosMap[pedidoId].itensMap[codigoProduto] = {
          codigoProduto: venda.codigoProduto,
          descricao:     venda.descricao,
          quantidade,
          valorUnitario,
          valorTotal,
          imagemBase64:  venda.imagemBase64 || venda.imagemURL || venda.imagem_base64 || venda.imagem_url,
        };
      } else {
        pedidosMap[pedidoId].itensMap[codigoProduto].quantidade  += quantidade;
        pedidosMap[pedidoId].itensMap[codigoProduto].valorTotal  += valorTotal;
      }

      pedidosMap[pedidoId].quantidadeTotal += quantidade;
      pedidosMap[pedidoId].desconto        += parseFloat(venda.valorDesconto) || 0;
    });

    Object.values(pedidosMap).forEach(pedido => {
      pedido.itens = Object.values(pedido.itensMap);
      delete pedido.itensMap;
      pedido.total = pedido.itens.reduce((acc, item) => acc + item.valorTotal, 0) + pedido.frete - pedido.desconto;
    });

    return Object.values(pedidosMap).sort((a, b) => {
      const diaA = String(a.dataVenda).substring(0, 10);
      const diaB = String(b.dataVenda).substring(0, 10);
      if (diaA !== diaB) return new Date(diaB) - new Date(diaA);
      return b.total - a.total;
    });
  }, []);

  const pedidosAgrupados = useMemo(() => ({
    centro:    agruparPorPedido(vendasLojaFisica.centro  || [], 'Centro'),
    caxias:    agruparPorPedido(vendasLojaFisica.caxias  || [], 'Caxias'),
    ecommerce: pedidosEcommerce,
  }), [vendasLojaFisica, pedidosEcommerce, agruparPorPedido]);

  // Pedidos e-commerce do status clicado, mapeados pro formato "boleta" do MovimentacoesModal
  // (mesmo design do detalhe da loja física). Memoizado p/ referência estável (evita re-fetch loop).
  const boletasStatusEcom = useMemo(() => {
    if (!modalStatusEcom.statusKey) return [];
    return (pedidosAgrupados.ecommerce || [])
      .filter(p => normalizarStatus(p.status) === modalStatusEcom.statusKey)
      .map(p => {
        const dv = String(p.dataVenda || '');
        return {
          boleta: p.id,
          data: dv.substring(0, 10),
          hora: (dv.match(/\d{2}:\d{2}(:\d{2})?/) || [''])[0],
          nomeCliente: p.cliente || null,
          totalItens: (p.itens || []).length,
          valorTotal: p.total || 0,
          itens: (p.itens || []).map(it => ({
            codigoProduto: it.codigoProduto,
            quantidade: it.quantidade,
            valorUnitario: it.valorUnitario,
            valorTotal: it.valorTotal,
          })),
        };
      });
  }, [modalStatusEcom.statusKey, pedidosAgrupados.ecommerce]);

  // ── Datas com vendas (para calendário) ────────────────────────────────────
  const datasComVendas = useMemo(() => {
    const todasVendas = [
      ...(vendasLojaFisica.centro  || []),
      ...(vendasLojaFisica.caxias  || []),
    ];
    const datasSet = new Set();
    todasVendas.forEach(v => {
      if (v.dataVenda) datasSet.add(String(v.dataVenda).substring(0, 10));
    });
    pedidosEcommerceRaw.forEach(p => {
      if (p.dataVenda) datasSet.add(String(p.dataVenda).substring(0, 10));
    });
    return Array.from(datasSet);
  }, [vendasLojaFisica, pedidosEcommerceRaw]);

  // ── Totais ────────────────────────────────────────────────────────────────

  // E-commerce: usar resumo.valorTotal da API (pedidos_ecommerce, fonte canônica)
  // statusBreakdown: atendido + emAberto (exclui cancelados)
  const apiTotalEcommerce  = (statusBreakdown.atendido.valor || 0) + (statusBreakdown.emAberto.valor || 0);
  const apiCountEcommerce  = (statusBreakdown.atendido.count || 0) + (statusBreakdown.emAberto.count || 0);

  // Fallback: se API retornou zero mas temos pedidos
  const pedidosFaturados   = pedidosEcommerce.filter(p => normalizarStatus(p.status) !== 'cancelado');
  const fallbackTotal      = pedidosFaturados.reduce((acc, p) => acc + (p.total || 0), 0);
  const fallbackCount      = pedidosFaturados.length;

  const totalEcommerce     = apiTotalEcommerce > 0 ? apiTotalEcommerce : fallbackTotal;
  const countEcommerce     = apiCountEcommerce > 0 ? apiCountEcommerce : fallbackCount;
  const ticketEcommerce    = countEcommerce > 0 ? totalEcommerce / countEcommerce : 0;

  const totalFreteEcommerce = pedidosFaturados.reduce((acc, p) => acc + (p.frete || 0), 0);

  // Loja física
  const subtotalCentro     = (vendasLojaFisica.centro  || []).reduce((acc, v) => acc + (parseFloat(v.valorTotal) || 0), 0);
  const descontosCentro    = (vendasLojaFisica.centro  || []).reduce((acc, v) => acc + (parseFloat(v.valorDesconto) || 0), 0);
  const totalCentro        = subtotalCentro - descontosCentro;
  const ticketCentro       = pedidosAgrupados.centro.length > 0
    ? totalCentro / pedidosAgrupados.centro.length : 0;

  // Breakdown da Loja Centro por loja física (001="353", 003="355") — para a tooltip.
  // (nomes idealmente viriam de um endpoint do lojasConfig; por ora espelhados aqui)
  const NOMES_LOJA_DETALHE = { '001': '353', '003': '355', '004': 'Caxias' };
  const CENTRO_CODIGOS = ['001', '003']; // Loja Centro = 353 (001) + 355 (003) — sempre mostrar as duas
  const breakdownCentro = (() => {
    const acc = {};
    // Sempre inicia 001 e 003 (mesmo zeradas) para a tooltip exibir a composição em qualquer dia
    for (const cod of CENTRO_CODIGOS) acc[cod] = { liquido: 0, boletas: new Set() };
    for (const v of (vendasLojaFisica.centro || [])) {
      const cod = String(v.codLocal || v.cod_local || '001');
      if (!acc[cod]) acc[cod] = { liquido: 0, boletas: new Set() };
      acc[cod].liquido += (parseFloat(v.valorTotal) || 0) - (parseFloat(v.valorDesconto) || 0);
      if (v.boleta) acc[cod].boletas.add(v.boleta);
    }
    return Object.entries(acc)
      .map(([cod, d]) => ({ cod, nome: NOMES_LOJA_DETALHE[cod] || cod, liquido: d.liquido, boletas: d.boletas.size }))
      .sort((a, b) => a.cod.localeCompare(b.cod));
  })();

  const subtotalCaxias     = (vendasLojaFisica.caxias  || []).reduce((acc, v) => acc + (parseFloat(v.valorTotal) || 0), 0);
  const descontosCaxias    = (vendasLojaFisica.caxias  || []).reduce((acc, v) => acc + (parseFloat(v.valorDesconto) || 0), 0);
  const totalCaxias        = subtotalCaxias - descontosCaxias;
  const ticketCaxias       = pedidosAgrupados.caxias.length > 0
    ? totalCaxias / pedidosAgrupados.caxias.length : 0;

  const totalGeral         = totalEcommerce + totalCentro + totalCaxias;

  // Movimentações loja física
  const movimentacoes      = vendasLojaFisica.movimentacoes || {
    centro: { cancelamentos: { valor: 0, quantidade: 0, boletas: 0 }, trocas: { valor: 0, quantidade: 0, boletas: 0 } },
    caxias: { cancelamentos: { valor: 0, quantidade: 0, boletas: 0 }, trocas: { valor: 0, quantidade: 0, boletas: 0 } },
  };
  const cancelamentosCentro = movimentacoes.centro?.cancelamentos || { valor: 0, quantidade: 0, boletas: 0 };
  const trocasCentro        = movimentacoes.centro?.trocas        || { valor: 0, quantidade: 0, boletas: 0 };
  const cancelamentosCaxias = movimentacoes.caxias?.cancelamentos || { valor: 0, quantidade: 0, boletas: 0 };
  const trocasCaxias        = movimentacoes.caxias?.trocas        || { valor: 0, quantidade: 0, boletas: 0 };

  // ── Breakdown de status e-commerce (para exibição na coluna) ─────────────
  const statusContagemEcommerce = useMemo(() => {
    const acc = {
      atendido:  { count: 0, valor: 0 },
      andamento: { count: 0, valor: 0 },
      aberto:    { count: 0, valor: 0 },
      cancelado: { count: 0, valor: 0 },
    };
    pedidosEcommerce.forEach(p => {
      const key = normalizarStatus(p.status);
      acc[key].count++;
      acc[key].valor += p.total || 0;
    });
    return acc;
  }, [pedidosEcommerce]);

  // ── Variações ▲/▼ ────────────────────────────────────────────────────────
  const variacaoEcommerce = useMemo(() => calcularVariacao(totalEcommerce, totaisAnteriores.ecommerce), [totalEcommerce, totaisAnteriores.ecommerce]);
  const variacaoCentro    = useMemo(() => calcularVariacao(totalCentro,    totaisAnteriores.centro),    [totalCentro,    totaisAnteriores.centro]);
  const variacaoCaxias    = useMemo(() => calcularVariacao(totalCaxias,    totaisAnteriores.caxias),    [totalCaxias,    totaisAnteriores.caxias]);
  const variacaoGeral     = useMemo(() => {
    const totalAnterior = (totaisAnteriores.ecommerce || 0) + (totaisAnteriores.centro || 0) + (totaisAnteriores.caxias || 0);
    return calcularVariacao(totalGeral, totalAnterior || null);
  }, [totalGeral, totaisAnteriores]);

  // ── Formatação ────────────────────────────────────────────────────────────

  const formatarData = (data) => {
    if (!data) return '';
    const dataStr = String(data).substring(0, 10);
    const [ano, mes, dia] = dataStr.split('-').map(Number);
    const d = new Date(ano, mes - 1, dia, 12, 0, 0);
    const hj = new Date();
    hj.setHours(12, 0, 0, 0);
    const ontem = new Date(hj);
    ontem.setDate(ontem.getDate() - 1);
    if (d.toDateString() === hj.toDateString())    return 'Hoje';
    if (d.toDateString() === ontem.toDateString()) return 'Ontem';
    return `${String(dia).padStart(2, '0')}/${String(mes).padStart(2, '0')}`;
  };

  const formatarPreco = (valor) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(valor || 0);

  // ── Toggle pedido expandido ───────────────────────────────────────────────
  const togglePedido = useCallback((pedidoId) => {
    setPedidosExpandidos(prev => ({ ...prev, [pedidoId]: !prev[pedidoId] }));
  }, []);

  // ── Renderizar item do pedido ─────────────────────────────────────────────
  const renderItemPedido = (item, loja, idx) => (
    <div key={`${item.codigoProduto}-${idx}`} className={styles.itemPedido}>
      <ProductImageTooltip
        codigo={item.codigoProduto}
        tipo={loja === 'E-commerce' ? 'ecommerce' : 'loja-fisica'}
      >
        <span className={styles.itemCodigo}>{item.codigoProduto}</span>
      </ProductImageTooltip>
      <span className={styles.itemDescricao} title={item.descricao}>
        {item.descricao?.length > 40 ? item.descricao.substring(0, 40) + '...' : item.descricao}
      </span>
      <span className={styles.itemQtd}>{item.quantidade}x</span>
      <span className={styles.itemValor}>{formatarPreco(item.valorTotal)}</span>
    </div>
  );

  // ── PedidoCard ────────────────────────────────────────────────────────────
  const PedidoCard = ({ pedido, cor }) => {
    const isExpanded = pedidosExpandidos[pedido.id];
    const statusKey  = normalizarStatus(pedido.status);
    const isCancelado = statusKey === 'cancelado';
    const cardRef    = useRef(null);

    const borderColor = statusKey === 'cancelado' ? '#e74c3c'
      : statusKey === 'andamento'                 ? '#f39c12'
      : statusKey === 'aberto'                    ? '#3498db'
      : cor;

    useEffect(() => {
      if (isExpanded && cardRef.current) {
        requestAnimationFrame(() => {
          cardRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
      }
    }, [isExpanded]);

    return (
      <div
        ref={cardRef}
        className={`${styles.pedidoCard} ${isExpanded ? styles.pedidoExpanded : ''} ${isCancelado ? styles.pedidoCancelado : ''}`}
        style={isCancelado ? { opacity: 0.65 } : {}}
      >
        <div
          className={styles.pedidoHeader}
          onClick={() => togglePedido(pedido.id)}
          style={{ borderLeftColor: borderColor }}
        >
          <div className={styles.pedidoInfo}>
            <div className={styles.pedidoDataRow}>
              <span className={styles.pedidoData}>{formatarData(pedido.dataVenda)}</span>
            </div>
            <span className={styles.pedidoId}>#{pedido.id}</span>
          </div>
          <div className={styles.pedidoResumo}>
            <span className={styles.pedidoItensCount}>{pedido.itens.length} {pedido.itens.length === 1 ? 'item' : 'itens'}</span>
            <span className={styles.pedidoDot}>·</span>
            <span className={styles.pedidoQtd}>{pedido.quantidadeTotal} peças</span>
          </div>
          <div className={styles.pedidoTotal}>
            <span className={`${styles.pedidoValor} ${isCancelado ? styles.valorCancelado : ''}`}>
              {isCancelado ? <s>{formatarPreco(pedido.total)}</s> : formatarPreco(pedido.total)}
            </span>
            {pedido.loja === 'E-commerce' && pedido.frete > 0 && (
              <span className={styles.pedidoFrete} title="Frete incluso">
                +{formatarPreco(pedido.frete)} frete
              </span>
            )}
            {pedido.loja !== 'E-commerce' && pedido.frete > 0 && (
              <span className={styles.pedidoFrete} title="Frete incluso">
                (Frete: {formatarPreco(pedido.frete)})
              </span>
            )}
            <svg
              className={`${styles.pedidoChevron} ${isExpanded ? styles.chevronUp : ''}`}
              xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>

        {isExpanded && (
          <div className={styles.pedidoItens}>
            <div className={styles.itensHeader}>
              <span>Código</span><span>Descrição</span><span>Qtd</span><span>Valor</span>
            </div>
            {pedido.itens.map((item, idx) => renderItemPedido(item, pedido.loja, idx))}

            {pedido.desconto > 0 && (
              <div className={styles.itemPedido} style={{ backgroundColor: '#fff3cd', color: '#856404', fontStyle: 'italic' }}>
                <span className={styles.itemCodigo}>DESC</span>
                <span className={styles.itemDescricao}>Desconto</span>
                <span className={styles.itemQtd}>-</span>
                <span className={styles.itemValor}>-{formatarPreco(pedido.desconto)}</span>
              </div>
            )}
            {pedido.frete > 0 && (
              <div className={styles.itemPedido} style={{ backgroundColor: '#f8f9fa', fontStyle: 'italic' }}>
                <span className={styles.itemCodigo}>FRETE</span>
                <span className={styles.itemDescricao}>Envio</span>
                <span className={styles.itemQtd}>-</span>
                <span className={styles.itemValor}>{formatarPreco(pedido.frete)}</span>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  // ── PedidosColuna ─────────────────────────────────────────────────────────
  const PedidosColuna = ({
    titulo,
    subtitulo,
    pedidos,
    avatarLetra,
    cor,
    ticketMedio,
    codLocal,
    nomeExibicao,
    cancelamentosResumo,
    trocasResumo,
    statusBreakdownColuna,   // apenas e-commerce
    isLojaFisica,            // Centro / Caxias
    expandida,
    onVerTodos,
  }) => (
    <div className={styles.vendasColuna}>
      <div className={styles.colunaHeader}>
        <div className={styles.colunaAvatar} style={{ background: `${cor}1f`, color: cor }}>
          {avatarLetra}
        </div>
        <div className={styles.colunaTituloContainer}>
          <h3 className={styles.colunaTitulo}>{titulo}</h3>
          <span className={styles.colunaCount}>
            {pedidos.length} {pedidos.length === 1 ? 'pedido' : 'pedidos'}
            {subtitulo && <> · {subtitulo}</>}
          </span>
        </div>
      </div>

      {/* Ticket Médio */}
      <div className={styles.ticketMedio}>
        <span className={styles.ticketLabel}>Ticket Médio</span>
        <span className={styles.ticketValor} style={{ color: cor }}>{formatarPreco(ticketMedio)}</span>
      </div>

      {/* Breakdown de status — e-commerce */}
      {statusBreakdownColuna && (
        <div style={{
          padding: '10px 20px',
          borderBottom: '1px solid #e8ecef',
          display: 'flex',
          flexWrap: 'wrap',
          gap: '6px',
        }}>
          {statusBreakdownColuna.atendido.count > 0 && (
            <span
              onClick={() => setModalStatusEcom({ show: true, statusKey: 'atendido' })}
              title="Ver pedidos atendidos"
              style={{
                fontSize: '11px', fontWeight: 600, color: '#27ae60',
                background: 'rgba(39,174,96,0.10)', border: '1px solid rgba(39,174,96,0.25)',
                borderRadius: '999px', padding: '2px 7px', cursor: 'pointer',
              }}>
              Atendido {statusBreakdownColuna.atendido.count}
            </span>
          )}
          {statusBreakdownColuna.andamento.count > 0 && (
            <span
              onClick={() => setModalStatusEcom({ show: true, statusKey: 'andamento' })}
              title="Ver pedidos em andamento"
              style={{
                fontSize: '11px', fontWeight: 600, color: '#f39c12',
                background: 'rgba(243,156,18,0.10)', border: '1px solid rgba(243,156,18,0.25)',
                borderRadius: '999px', padding: '2px 7px', cursor: 'pointer',
              }}>
              Andamento {statusBreakdownColuna.andamento.count}
            </span>
          )}
          {statusBreakdownColuna.aberto.count > 0 && (
            <span
              onClick={() => setModalStatusEcom({ show: true, statusKey: 'aberto' })}
              title="Ver pedidos em aberto"
              style={{
                fontSize: '11px', fontWeight: 600, color: '#3498db',
                background: 'rgba(52,152,219,0.10)', border: '1px solid rgba(52,152,219,0.25)',
                borderRadius: '999px', padding: '2px 7px', cursor: 'pointer',
              }}>
              Em Aberto {statusBreakdownColuna.aberto.count}
            </span>
          )}
          {statusBreakdownColuna.cancelado.count > 0 && (
            <span
              onClick={() => setModalStatusEcom({ show: true, statusKey: 'cancelado' })}
              title="Ver pedidos cancelados"
              style={{
                fontSize: '11px', fontWeight: 600, color: '#e74c3c',
                background: 'rgba(231,76,60,0.10)', border: '1px solid rgba(231,76,60,0.25)',
                borderRadius: '999px', padding: '2px 7px', cursor: 'pointer',
              }}>
              Cancelado {statusBreakdownColuna.cancelado.count}
            </span>
          )}
        </div>
      )}

      {/* Breakdown de status — loja física (mesmo design do e-commerce: pílulas abaixo do ticket) */}
      {isLojaFisica && (
        <div style={{
          padding: '10px 20px',
          borderBottom: '1px solid #e8ecef',
          display: 'flex',
          flexWrap: 'wrap',
          gap: '6px',
        }}>
          <span style={{
            fontSize: '11px', fontWeight: 600, color: '#27ae60',
            background: 'rgba(39,174,96,0.10)', border: '1px solid rgba(39,174,96,0.25)',
            borderRadius: '999px', padding: '2px 7px',
          }}>
            Concluídas {pedidos.length}
          </span>
          {cancelamentosResumo?.boletas > 0 && (
            <span
              onClick={() => setModalMovimentacoes({ show: true, tipo: 'cancelamentos', loja: nomeExibicao, codLocal })}
              title="Ver cancelamentos do período"
              style={{
                fontSize: '11px', fontWeight: 600, color: '#e74c3c',
                background: 'rgba(231,76,60,0.10)', border: '1px solid rgba(231,76,60,0.25)',
                borderRadius: '999px', padding: '2px 7px', cursor: 'pointer',
              }}>
              Cancelados {cancelamentosResumo.boletas}
            </span>
          )}
          {trocasResumo?.boletas > 0 && (
            <span
              onClick={() => setModalMovimentacoes({ show: true, tipo: 'trocas', loja: nomeExibicao, codLocal })}
              title="Ver trocas do período"
              style={{
                fontSize: '11px', fontWeight: 600, color: '#f39c12',
                background: 'rgba(243,156,18,0.10)', border: '1px solid rgba(243,156,18,0.25)',
                borderRadius: '999px', padding: '2px 7px', cursor: 'pointer',
              }}>
              Trocas {trocasResumo.boletas}
            </span>
          )}
        </div>
      )}

      <div className={`${styles.pedidosLista} ${expandida ? styles.pedidosListaExpandida : ''}`}>
        {loading ? (
          <div className={styles.loadingContainer}>
            <div className={styles.spinner}></div>
            <span>Carregando...</span>
          </div>
        ) : pedidos.length === 0 ? (
          <div className={styles.semVendas}>
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
            </svg>
            <span>Nenhum pedido no período</span>
          </div>
        ) : (
          pedidos.map(pedido => (
            <PedidoCard key={pedido.id} pedido={pedido} cor={cor} />
          ))
        )}
      </div>

      {/* "Ver todos os pedidos" — tira o limite de altura/scroll interno da lista acima
          (todos os pedidos já estão carregados; isso só remove a caixinha de rolagem). */}
      {!loading && !expandida && pedidos.length > 0 && (
        <button className={styles.verTodosBtn} style={{ color: cor }} onClick={onVerTodos}>
          Ver todos os pedidos
        </button>
      )}
    </div>
  );

  // ── período anterior formatado (para tooltip nos cards) ───────────────────
  const periodoAnterior = useMemo(() => calcularPeriodoAnterior(dataInicio, dataFim), [dataInicio, dataFim]);
  const labelPeriodoAnterior = `${periodoAnterior.dataInicio.split('-').reverse().join('/')} a ${periodoAnterior.dataFim.split('-').reverse().join('/')}`;

  // ── render ────────────────────────────────────────────────────────────────
  return (
    <>
    <div className={styles.container}>
      <div className={styles.vendasSection}>
        <div className={styles.pageHeader}>
          <div className={styles.pageHeaderTitles}>
            <h2 className={styles.pageTitle}>Consolidado 3 Lojas</h2>
            <p className={styles.pageSubtitle}>Vendas por canal no período selecionado</p>
          </div>

          <div className={styles.periodoBar}>
            <span className={styles.periodoLabel}>PERÍODO</span>
            <DatePickerWithData
              value={dataInicio}
              onChange={setDataInicio}
              datasComDados={datasComVendas}
            />
            <span className={styles.periodoSep}>até</span>
            <DatePickerWithData
              value={dataFim}
              onChange={setDataFim}
              datasComDados={datasComVendas}
            />
            <button onClick={carregarVendas} disabled={loading} className={styles.btnBuscarPeriodo}>
              {loading ? 'Buscando...' : 'Buscar período'}
            </button>
            {ultimaAtualizacao && !loading && (
              <span className={styles.ultimaBusca}>
                Última busca: {ultimaAtualizacao.toLocaleTimeString('pt-BR')}
              </span>
            )}
          </div>
        </div>

        {/* Cards de Resumo (mockup 2026-08-25: 4 cards — 3 canais + Total Geral) */}
        <div className={styles.resumoGrid}>
          {/* ── E-commerce ── */}
          <ResumoCard
            titulo="E-commerce"
            valor={formatarPreco(totalEcommerce)}
            variacaoPct={variacaoEcommerce}
            subtitulo={`${pedidosAgrupados.ecommerce.length} ${pedidosAgrupados.ecommerce.length === 1 ? 'pedido' : 'pedidos'}`}
            tooltip={
                <div style={{ minWidth: '240px' }}>
                  {statusContagemEcommerce.atendido.count > 0 && (
                    <div style={{ marginBottom: '6px', paddingBottom: '6px', borderBottom: '1px solid rgba(255,255,255,0.1)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span><span style={{ color: '#27ae60', fontWeight: 700 }}>● Atendido</span> <span style={{ opacity: 0.6, fontSize: '0.82em' }}>({statusContagemEcommerce.atendido.count})</span></span>
                      <span style={{ color: '#27ae60', fontWeight: 600 }}>{formatarPreco(statusContagemEcommerce.atendido.valor)}</span>
                    </div>
                  )}
                  {statusContagemEcommerce.andamento.count > 0 && (
                    <div style={{ marginBottom: '6px', paddingBottom: '6px', borderBottom: '1px solid rgba(255,255,255,0.1)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span><span style={{ color: '#f39c12', fontWeight: 700 }}>● Em Andamento</span> <span style={{ opacity: 0.6, fontSize: '0.82em' }}>({statusContagemEcommerce.andamento.count})</span></span>
                      <span style={{ color: '#f39c12', fontWeight: 600 }}>{formatarPreco(statusContagemEcommerce.andamento.valor)}</span>
                    </div>
                  )}
                  {statusContagemEcommerce.aberto.count > 0 && (
                    <div style={{ marginBottom: '6px', paddingBottom: '6px', borderBottom: '1px solid rgba(255,255,255,0.1)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span><span style={{ color: '#3498db', fontWeight: 700 }}>● Em Aberto</span> <span style={{ opacity: 0.6, fontSize: '0.82em' }}>({statusContagemEcommerce.aberto.count})</span></span>
                      <span style={{ color: '#3498db', fontWeight: 600 }}>{formatarPreco(statusContagemEcommerce.aberto.valor)}</span>
                    </div>
                  )}
                  {statusContagemEcommerce.cancelado.count > 0 && (
                    <div style={{ marginBottom: '6px', paddingBottom: '6px', borderBottom: '1px solid rgba(255,255,255,0.1)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span><span style={{ color: '#e74c3c', fontWeight: 700 }}>● Cancelado</span> <span style={{ opacity: 0.6, fontSize: '0.82em' }}>({statusContagemEcommerce.cancelado.count})</span></span>
                      <span style={{ color: '#e74c3c', fontWeight: 600, textDecoration: 'line-through' }}>{formatarPreco(statusContagemEcommerce.cancelado.valor)}</span>
                    </div>
                  )}
                  {totalFreteEcommerce > 0 && (
                    <div style={{ paddingTop: '4px', display: 'flex', justifyContent: 'space-between', fontSize: '0.85em', opacity: 0.75 }}>
                      <span>Frete total</span>
                      <span>{formatarPreco(totalFreteEcommerce)}</span>
                    </div>
                  )}
                  {variacaoEcommerce !== null && (
                    <div style={{ marginTop: '8px', paddingTop: '6px', borderTop: '1px solid rgba(255,255,255,0.1)', fontSize: '0.82em', opacity: 0.7 }}>
                      vs mesmo período do ano passado ({labelPeriodoAnterior}): {formatarPreco(totaisAnteriores.ecommerce || 0)}
                    </div>
                  )}
                </div>
              }
            />

          {/* ── Loja Centro ── */}
          <ResumoCard
            titulo="Loja Centro"
            valor={formatarPreco(totalCentro)}
            variacaoPct={variacaoCentro}
            subtitulo={`${pedidosAgrupados.centro.length} ${pedidosAgrupados.centro.length === 1 ? 'pedido' : 'pedidos'}`}
            tooltip={
                <div style={{ minWidth: '240px' }}>
                  <div style={{ marginBottom: '8px', paddingBottom: '6px', borderBottom: '1px solid rgba(255,255,255,0.15)' }}>
                    <strong>Subtotal:</strong> {formatarPreco(subtotalCentro)}
                  </div>
                  {descontosCentro > 0 && (
                    <div style={{ color: '#ffd43b', marginBottom: '4px' }}>
                      <strong>Desc. Boleta:</strong> -{formatarPreco(descontosCentro)}
                    </div>
                  )}
                  {cancelamentosCentro.valor > 0 && (
                    <div style={{ color: '#ff6b6b', marginBottom: '4px' }}>
                      <strong>Cancelamentos:</strong> -{formatarPreco(cancelamentosCentro.valor)}
                      <span style={{ opacity: 0.6, marginLeft: '6px', fontSize: '0.85em' }}>({cancelamentosCentro.boletas} bol.)</span>
                    </div>
                  )}
                  {trocasCentro.valor > 0 && (
                    <div style={{ color: '#ffa94d', marginBottom: '4px' }}>
                      <strong>Trocas:</strong> -{formatarPreco(trocasCentro.valor)}
                      <span style={{ opacity: 0.6, marginLeft: '6px', fontSize: '0.85em' }}>({trocasCentro.boletas} bol.)</span>
                    </div>
                  )}
                  <div style={{ marginTop: '6px', paddingTop: '6px', borderTop: '1px solid rgba(255,255,255,0.15)', color: '#51cf66' }}>
                    <strong>Liquido:</strong> {formatarPreco(totalCentro)}
                  </div>
                  {breakdownCentro.length > 0 && (
                    <div style={{ marginTop: '8px', paddingTop: '6px', borderTop: '1px solid rgba(255,255,255,0.12)' }}>
                      <div style={{ fontSize: '0.74em', opacity: 0.55, marginBottom: '4px' }}>Composição da Loja Centro</div>
                      <div style={{ display: 'flex', gap: '12px' }}>
                        {breakdownCentro.map(b => (
                          <div key={b.cod} style={{ flex: 1 }}>
                            <div style={{ fontSize: '0.78em', opacity: 0.65 }}>Loja {b.nome}</div>
                            <div style={{ fontWeight: 600, color: '#74c0fc' }}>{formatarPreco(b.liquido)}</div>
                            <div style={{ fontSize: '0.76em', opacity: 0.6 }}>{b.boletas} boletas</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {variacaoCentro !== null && (
                    <div style={{ marginTop: '8px', paddingTop: '6px', borderTop: '1px solid rgba(255,255,255,0.1)', fontSize: '0.82em', opacity: 0.7 }}>
                      vs mesmo período do ano passado ({labelPeriodoAnterior}): {formatarPreco(totaisAnteriores.centro || 0)}
                    </div>
                  )}
                </div>
              }
            />

          {/* ── Loja Caxias ── */}
          <ResumoCard
            titulo="Loja Caxias"
            valor={formatarPreco(totalCaxias)}
            variacaoPct={variacaoCaxias}
            subtitulo={`${pedidosAgrupados.caxias.length} ${pedidosAgrupados.caxias.length === 1 ? 'pedido' : 'pedidos'}`}
            tooltip={
                <div style={{ minWidth: '240px' }}>
                  <div style={{ marginBottom: '8px', paddingBottom: '6px', borderBottom: '1px solid rgba(255,255,255,0.15)' }}>
                    <strong>Subtotal:</strong> {formatarPreco(subtotalCaxias)}
                  </div>
                  {descontosCaxias > 0 && (
                    <div style={{ color: '#ffd43b', marginBottom: '4px' }}>
                      <strong>Desc. Geral:</strong> -{formatarPreco(descontosCaxias)}
                    </div>
                  )}
                  {cancelamentosCaxias.valor > 0 && (
                    <div style={{ color: '#ff6b6b', marginBottom: '4px' }}>
                      <strong>Cancelamentos:</strong> -{formatarPreco(cancelamentosCaxias.valor)}
                      <span style={{ opacity: 0.6, marginLeft: '6px', fontSize: '0.85em' }}>({cancelamentosCaxias.boletas} bol.)</span>
                    </div>
                  )}
                  {trocasCaxias.valor > 0 && (
                    <div style={{ color: '#ffa94d', marginBottom: '4px' }}>
                      <strong>Trocas:</strong> -{formatarPreco(trocasCaxias.valor)}
                      <span style={{ opacity: 0.6, marginLeft: '6px', fontSize: '0.85em' }}>({trocasCaxias.boletas} bol.)</span>
                    </div>
                  )}
                  <div style={{ marginTop: '6px', paddingTop: '6px', borderTop: '1px solid rgba(255,255,255,0.15)', color: '#51cf66' }}>
                    <strong>Liquido:</strong> {formatarPreco(totalCaxias)}
                  </div>
                  {variacaoCaxias !== null && (
                    <div style={{ marginTop: '8px', paddingTop: '6px', borderTop: '1px solid rgba(255,255,255,0.1)', fontSize: '0.82em', opacity: 0.7 }}>
                      vs mesmo período do ano passado ({labelPeriodoAnterior}): {formatarPreco(totaisAnteriores.caxias || 0)}
                    </div>
                  )}
                </div>
              }
            />

          {/* ── Total Geral ── */}
          <ResumoCard
            dark
            titulo={`Total Geral (${Math.ceil(Math.abs(new Date(dataFim) - new Date(dataInicio)) / (1000 * 60 * 60 * 24)) + 1} dias)`}
            valor={formatarPreco(totalGeral)}
            variacaoPct={variacaoGeral}
            subtitulo="soma dos 3 canais"
          />
        </div>

        {/* Grid de Pedidos */}
        <div className={styles.vendasGrid}>
          <PedidosColuna
            titulo="E-commerce"
            subtitulo="fonte: pedidos_ecommerce"
            pedidos={pedidosAgrupados.ecommerce}
            avatarLetra="E"
            cor="#9b59b6"
            ticketMedio={ticketEcommerce}
            isEcommerce={true}
            statusBreakdownColuna={statusContagemEcommerce}
            expandida={!!colunasExpandidas.ecommerce}
            onVerTodos={() => setColunasExpandidas(prev => ({ ...prev, ecommerce: true }))}
          />

          <PedidosColuna
            titulo="Loja Centro"
            subtitulo="vendas Go2"
            pedidos={pedidosAgrupados.centro}
            avatarLetra="C"
            cor="#004B87"
            ticketMedio={ticketCentro}
            codLocal="001"
            nomeExibicao="Centro"
            cancelamentosResumo={cancelamentosCentro}
            trocasResumo={trocasCentro}
            isLojaFisica={true}
            expandida={!!colunasExpandidas.centro}
            onVerTodos={() => setColunasExpandidas(prev => ({ ...prev, centro: true }))}
          />

          <PedidosColuna
            titulo="Loja Caxias"
            subtitulo="vendas Go2"
            pedidos={pedidosAgrupados.caxias}
            avatarLetra="X"
            cor="#27ae60"
            ticketMedio={ticketCaxias}
            codLocal="004"
            nomeExibicao="Caxias"
            cancelamentosResumo={cancelamentosCaxias}
            trocasResumo={trocasCaxias}
            isLojaFisica={true}
            expandida={!!colunasExpandidas.caxias}
            onVerTodos={() => setColunasExpandidas(prev => ({ ...prev, caxias: true }))}
          />
        </div>
      </div>
    </div>

    <MovimentacoesModal
      show={modalMovimentacoes.show}
      onHide={() => setModalMovimentacoes(prev => ({ ...prev, show: false }))}
      tipo={modalMovimentacoes.tipo}
      loja={modalMovimentacoes.loja}
      codLocal={modalMovimentacoes.codLocal}
      startDate={dataInicio}
      endDate={dataFim}
    />

    {/* Detalhe de status do e-commerce — MESMO design do detalhe da loja física (MovimentacoesModal).
        Clicar num badge (Atendido/Andamento/Em Aberto/Cancelado) lista os pedidos daquele status. */}
    <MovimentacoesModal
      show={modalStatusEcom.show}
      onHide={() => setModalStatusEcom(prev => ({ ...prev, show: false }))}
      dadosPreCarregados={boletasStatusEcom}
      titulo={STATUS_CONFIG[modalStatusEcom.statusKey]?.label || 'Pedidos'}
      cor={STATUS_CONFIG[modalStatusEcom.statusKey]?.cor || '#9b59b6'}
      icone="🛒"
      unidade={boletasStatusEcom.length === 1 ? 'pedido' : 'pedidos'}
      negativo={false}
      loja="E-commerce"
      startDate={dataInicio}
      endDate={dataFim}
    />
    </>
  );
};

export default Home;
